import Iconify from 'src/components/Iconify';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { Badge } from 'src/@/components/ui/badge';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Separator } from 'src/@/components/ui/separator';
import { Switch } from 'src/@/components/ui/switch';
import { Textarea } from 'src/@/components/ui/textarea';

const DIAS_SEMANA = [
    { key: 'seg', label: 'Segunda-feira' },
    { key: 'ter', label: 'Terça-feira' },
    { key: 'qua', label: 'Quarta-feira' },
    { key: 'qui', label: 'Quinta-feira' },
    { key: 'sex', label: 'Sexta-feira' },
    { key: 'sab', label: 'Sábado' },
    { key: 'dom', label: 'Domingo' },
];

const DEFAULT_HORARIOS = {
    seg: { ativo: true, de: '09:00', ate: '18:00' },
    ter: { ativo: true, de: '09:00', ate: '18:00' },
    qua: { ativo: true, de: '09:00', ate: '18:00' },
    qui: { ativo: true, de: '09:00', ate: '18:00' },
    sex: { ativo: true, de: '09:00', ate: '18:00' },
    sab: { ativo: false, de: '09:00', ate: '13:00' },
    dom: { ativo: false, de: '09:00', ate: '13:00' },
};

// IDs são estáveis e persistidos no MongoDB — labels podem ser reordenados/editados
// sem impacto no agente (o backend só consulta pelo id).
// i18nKey identifica a chave de tradução em settings:aiAgents.realstate.locacaoVenda.garantias.<i18nKey> —
// mantido separado de `id` (kebab-case, incompatível com a convenção camelCase de chaves i18n) pra não
// acoplar o texto exibido ao identificador persistido.
const GARANTIAS = [
    { id: 'fiador', i18nKey: 'fiador', label: 'Fiador' },
    { id: 'seguro-fianca', i18nKey: 'seguroFianca', label: 'Seguro fiança' },
    { id: 'caucao', i18nKey: 'caucao', label: 'Depósito caução' },
    { id: 'titulo-capitalizacao', i18nKey: 'tituloCapitalizacao', label: 'Título de capitalização' },
    { id: 'fiador-empresa', i18nKey: 'fiadorEmpresa', label: 'Carta de fiança bancária' },
    { id: 'fiador-proprio', i18nKey: 'fiadorProprio', label: 'Fiador próprio' },
];

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

function HorariosEditor({ horarios, onChange }) {
    const { t } = useTranslation('settings');
    const update = (dia, patch) => {
        onChange({
            ...horarios,
            [dia]: { ...(horarios[dia] || DEFAULT_HORARIOS[dia]), ...patch },
        });
    };

    return (
        <div className="flex flex-col gap-2">
            {DIAS_SEMANA.map(({ key, label }) => {
                const h = horarios[key] || DEFAULT_HORARIOS[key];
                return (
                    <div
                        key={key}
                        className="grid grid-cols-[140px_48px_1fr_auto_1fr] items-center gap-3 py-2 px-3 rounded-xl bg-gray-50/60"
                    >
                        <span className="text-sm font-medium text-gray-700">{t(`aiAgents.realstate.locacaoVenda.days.${key}`, label)}</span>
                        <Switch
                            checked={!!h.ativo}
                            onCheckedChange={(value) => update(key, { ativo: value })}
                        />
                        <Input
                            type="time"
                            value={h.de || '09:00'}
                            disabled={!h.ativo}
                            onChange={(e) => update(key, { de: e.target.value })}
                            className={`${INPUT_CLS} ${!h.ativo ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                        />
                        <span className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.horarios.until', 'até')}</span>
                        <Input
                            type="time"
                            value={h.ate || '18:00'}
                            disabled={!h.ativo}
                            onChange={(e) => update(key, { ate: e.target.value })}
                            className={`${INPUT_CLS} ${!h.ativo ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function GarantiasPicker({ selected, onChange }) {
    const { t } = useTranslation('settings');
    const toggle = (id) => {
        const has = selected.includes(id);
        onChange(has ? selected.filter((g) => g !== id) : [...selected, id]);
    };

    return (
        <div className="flex flex-wrap gap-2">
            {GARANTIAS.map((g) => {
                const active = selected.includes(g.id);
                return (
                    <button
                        key={g.id}
                        type="button"
                        onClick={() => toggle(g.id)}
                        className={`transition-all active:transform active:scale-95 ${active ? '' : 'opacity-70 hover:opacity-100'}`}
                    >
                        <Badge
                            variant="outline"
                            className={`text-xs font-medium flex items-center gap-1.5 cursor-pointer ${
                                active
                                    ? 'bg-violet-50 text-violet-700 border-violet-200'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-violet-200'
                            }`}
                        >
                            <Iconify
                                icon={active ? 'eva:checkmark-circle-2-fill' : 'eva:radio-button-off-outline'}
                                width={14}
                                className={active ? 'text-violet-600' : 'text-gray-400'}
                            />
                            {t(`aiAgents.realstate.locacaoVenda.garantias.${g.i18nKey}`, g.label)}
                        </Badge>
                    </button>
                );
            })}
        </div>
    );
}

export default function LocacaoVendaSection({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const modRealstate = aiAgent?.config?.module?.realstate || {};
    const locacao = modRealstate.locacao || {};
    const venda = modRealstate.venda || {};

    const updateLocacao = (patch) => {
        changeModuleConfig({ locacao: { ...locacao, ...patch } });
    };
    const updateVenda = (patch) => {
        changeModuleConfig({ venda: { ...venda, ...patch } });
    };

    const locacaoGarantias = Array.isArray(locacao.garantias) ? locacao.garantias : [];
    const locacaoHorarios = locacao.horarios || DEFAULT_HORARIOS;
    const vendaHorarios = venda.horarios || DEFAULT_HORARIOS;

    return (
        <Accordion type="multiple" defaultValue={[]} className="flex flex-col gap-4">
            {/* ─── Locação ─────────────────────────────────────────── */}
            <AccordionItem
                value="locacao"
                className="relative rounded-xl border border-gray-100 bg-white overflow-hidden"
            >
                <AccordionTrigger className="px-5 py-4 hover:no-underline pr-20">
                    <div className="flex items-center gap-3 flex-1 text-left">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 shrink-0">
                            <Iconify icon="mdi:key-outline" width={20} className="text-violet-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-900">{t('aiAgents.realstate.locacaoVenda.locacao.title', 'Locação')}</h3>
                            <p className="text-sm font-normal text-gray-500 mt-0.5">{t('aiAgents.realstate.locacaoVenda.locacao.description', 'A Lais atende demandas de aluguel e informa documentação, garantias, prazos e multas.')}</p>
                        </div>
                    </div>
                </AccordionTrigger>
                <div
                    className="absolute right-12 top-5"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <Switch
                        checked={!!locacao.ativo}
                        onCheckedChange={(value) => updateLocacao({ ativo: value })}
                    />
                </div>
                <AccordionContent className="px-5 pb-5">
                    {locacao.ativo ? (
                        <div className="flex flex-col gap-5">
                            <div className="flex flex-col gap-2">
                                <h4 className="text-sm font-semibold text-gray-900">{t('aiAgents.realstate.locacaoVenda.locacao.scheduleTitle', 'Horário de atendimento (Locação)')}</h4>
                                <p className="text-xs text-gray-500">{t('aiAgents.realstate.locacaoVenda.locacao.scheduleHint', 'Habilite os dias em que a Lais aceita demandas de locação.')}</p>
                            </div>
                            <HorariosEditor
                                horarios={locacaoHorarios}
                                onChange={(h) => updateLocacao({ horarios: h })}
                            />

                            <Separator className="bg-gray-200/60" />

                            <div className="flex flex-col gap-2">
                                <Label className={LABEL_CLS}>{t('aiAgents.realstate.locacaoVenda.locacao.documentationLabel', 'Documentação para alugar')}</Label>
                                <Textarea
                                    value={locacao.documentacao || ''}
                                    onChange={(e) => updateLocacao({ documentacao: e.target.value })}
                                    placeholder={t('aiAgents.realstate.locacaoVenda.locacao.documentationPlaceholder', 'Ex.: RG, CPF, comprovante de residência e de renda (3 últimos), declaração de IR, referências pessoais.')}
                                    rows={4}
                                    className={INPUT_CLS}
                                />
                                <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.locacao.documentationHint', 'Liste o mínimo necessário — a Lais reproduz essa informação para o cliente.')}</p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label className={LABEL_CLS}>{t('aiAgents.realstate.locacaoVenda.locacao.garantiasLabel', 'Garantias aceitas')}</Label>
                                <GarantiasPicker
                                    selected={locacaoGarantias}
                                    onChange={(g) => updateLocacao({ garantias: g })}
                                />
                                <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.locacao.garantiasHint', 'Clique em cada modalidade para habilitar.')}</p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label className={LABEL_CLS}>{t('aiAgents.realstate.locacaoVenda.locacao.termsLabel', 'Prazos e multas')}</Label>
                                <Textarea
                                    value={locacao.prazosMultas || ''}
                                    onChange={(e) => updateLocacao({ prazosMultas: e.target.value })}
                                    placeholder={t('aiAgents.realstate.locacaoVenda.locacao.termsPlaceholder', 'Ex.: Multa rescisória proporcional a 3 aluguéis. Prazo de análise: até 48h úteis.')}
                                    rows={3}
                                    className={INPUT_CLS}
                                />
                            </div>

                            <div className="flex flex-col gap-2 max-w-md">
                                <Label className={LABEL_CLS}>{t('aiAgents.realstate.locacaoVenda.professional.label', 'Nomenclatura do profissional')}</Label>
                                <Input
                                    value={locacao.nomenclaturaCorretor || ''}
                                    onChange={(e) => updateLocacao({ nomenclaturaCorretor: e.target.value })}
                                    placeholder={t('aiAgents.realstate.locacaoVenda.locacao.professionalPlaceholder', 'Consultor, Especialista, Atendente...')}
                                    className={INPUT_CLS}
                                />
                                <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.professional.hint', 'Como a Lais se refere ao humano que assume a conversa. Padrão: "consultor".')}</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.locacao.disabledHint', 'Ative o módulo de locação para configurar horários, documentação e garantias.')}</p>
                    )}
                </AccordionContent>
            </AccordionItem>

            {/* ─── Venda ────────────────────────────────────────────── */}
            <AccordionItem
                value="venda"
                className="relative rounded-xl border border-gray-100 bg-white overflow-hidden"
            >
                <AccordionTrigger className="px-5 py-4 hover:no-underline pr-20">
                    <div className="flex items-center gap-3 flex-1 text-left">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50 shrink-0">
                            <Iconify icon="mdi:home-variant-outline" width={20} className="text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-900">{t('aiAgents.realstate.locacaoVenda.venda.title', 'Venda')}</h3>
                            <p className="text-sm font-normal text-gray-500 mt-0.5">{t('aiAgents.realstate.locacaoVenda.venda.description', 'A Lais atende demandas de compra e informa documentação e condições de financiamento.')}</p>
                        </div>
                    </div>
                </AccordionTrigger>
                <div
                    className="absolute right-12 top-5"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <Switch
                        checked={!!venda.ativo}
                        onCheckedChange={(value) => updateVenda({ ativo: value })}
                    />
                </div>
                <AccordionContent className="px-5 pb-5">
                    {venda.ativo ? (
                        <div className="flex flex-col gap-5">
                            <div className="flex flex-col gap-2">
                                <h4 className="text-sm font-semibold text-gray-900">{t('aiAgents.realstate.locacaoVenda.venda.scheduleTitle', 'Horário de atendimento (Venda)')}</h4>
                                <p className="text-xs text-gray-500">{t('aiAgents.realstate.locacaoVenda.venda.scheduleHint', 'Habilite os dias em que a Lais aceita demandas de venda.')}</p>
                            </div>
                            <HorariosEditor
                                horarios={vendaHorarios}
                                onChange={(h) => updateVenda({ horarios: h })}
                            />

                            <Separator className="bg-gray-200/60" />

                            <div className="flex flex-col gap-2">
                                <Label className={LABEL_CLS}>{t('aiAgents.realstate.locacaoVenda.venda.documentationLabel', 'Documentação para comprar')}</Label>
                                <Textarea
                                    value={venda.documentacao || ''}
                                    onChange={(e) => updateVenda({ documentacao: e.target.value })}
                                    placeholder={t('aiAgents.realstate.locacaoVenda.venda.documentationPlaceholder', 'Ex.: RG, CPF, comprovantes de residência e renda, certidões negativas, carta de pré-aprovação bancária.')}
                                    rows={4}
                                    className={INPUT_CLS}
                                />
                                <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.venda.documentationHint', 'Ensine a Lais quais documentos são necessários para concretizar a venda.')}</p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label className={LABEL_CLS}>{t('aiAgents.realstate.locacaoVenda.venda.financingLabel', 'Financiamento')}</Label>
                                <Textarea
                                    value={venda.financiamento || ''}
                                    onChange={(e) => updateVenda({ financiamento: e.target.value })}
                                    placeholder={t('aiAgents.realstate.locacaoVenda.venda.financingPlaceholder', 'Ex.: Simulamos com Itaú, Caixa e Bradesco. Retorno em até 24h úteis após envio dos documentos.')}
                                    rows={3}
                                    className={INPUT_CLS}
                                />
                                <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.venda.financingHint', 'Bancos parceiros, prazos médios e se há simulação.')}</p>
                            </div>

                            <div className="flex flex-col gap-2 max-w-md">
                                <Label className={LABEL_CLS}>{t('aiAgents.realstate.locacaoVenda.professional.label', 'Nomenclatura do profissional')}</Label>
                                <Input
                                    value={venda.nomenclaturaCorretor || ''}
                                    onChange={(e) => updateVenda({ nomenclaturaCorretor: e.target.value })}
                                    placeholder={t('aiAgents.realstate.locacaoVenda.venda.professionalPlaceholder', 'Corretor, Consultor de Vendas...')}
                                    className={INPUT_CLS}
                                />
                                <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.professional.hint', 'Como a Lais se refere ao humano que assume a conversa. Padrão: "consultor".')}</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400">{t('aiAgents.realstate.locacaoVenda.venda.disabledHint', 'Ative o módulo de venda para configurar horários, documentação e financiamento.')}</p>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
